import React, { useEffect, useState } from 'react';
import { dashboard, roastedStock, greenCategories, coffeeDispatches, roastRecords, clients as clientsApi, expenses as expensesApi } from '../lib/api';
import { Coffee, Flame, Truck, AlertTriangle, TrendingUp, Download, Package, IndianRupee, Users, Wallet, FileBarChart } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Cell, ComposedChart, Legend } from 'recharts';
import { downloadPdfReport } from '../lib/pdfReport';
import { inr, rs } from '../lib/format';
import { MonthlyReportModal } from '../components/MonthlyReportModal';
import { format, subDays, startOfDay, subMonths, startOfMonth } from 'date-fns';

const CAT_COLORS = ['#f59e0b', '#10b981', '#6366f1', '#ec4899', '#06b6d4', '#8b5cf6', '#f43f5e', '#14b8a6'];

const Dashboard = () => {
  const [summary, setSummary] = useState(null);
  const [rstock, setRstock] = useState([]);
  const [cats, setCats] = useState([]);
  const [dispatches, setDispatches] = useState([]);
  const [roasts, setRoasts] = useState([]);
  const [clientRows, setClientRows] = useState([]);
  const [expenseList, setExpenseList] = useState([]);
  const [costs, setCosts] = useState([]);
  const [stockRows, setStockRows] = useState([]);
  const [showReport, setShowReport] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, rs2, c, d, r, cl, ex, k, st] = await Promise.all([
          dashboard.getSummary(),
          roastedStock.getSummary(),
          greenCategories.getAll(),
          coffeeDispatches.getAll(),
          roastRecords.getAll(),
          clientsApi.getAll(),
          expensesApi.getAll(),
          coffeeDispatches.getCosts(),
          roastedStock.getAll(),
        ]);
        setSummary(s.data);
        setRstock(rs2.data);
        setCats(c.data);
        setDispatches(d.data);
        setRoasts(r.data);
        setClientRows(cl.data);
        setExpenseList(ex.data);
        setCosts(k.data);
        setStockRows(st.data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading || !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  // Last 14 days roasted trend
  const now = new Date();
  const fourteenDays = Array.from({ length: 14 }).map((_, i) => {
    const d = startOfDay(subDays(now, 13 - i));
    return { key: format(d, 'yyyy-MM-dd'), label: format(d, 'MMM dd'), value: 0 };
  });
  roasts.forEach((r) => {
    const k = format(new Date(r.roast_date), 'yyyy-MM-dd');
    const bucket = fourteenDays.find((b) => b.key === k);
    if (bucket) bucket.value += r.roasted_output_kg;
  });

  // Monthly sales / cost / profit — last 6 months
  const profitTrend = Array.from({ length: 6 }).map((_, i) => {
    const m = startOfMonth(subMonths(now, 5 - i));
    return { key: format(m, 'yyyy-MM'), label: format(m, 'MMM yy'), sales: 0, cost: 0, profit: 0 };
  });
  dispatches.forEach((d) => {
    const bucket = profitTrend.find((b) => b.key === format(new Date(d.dispatch_date), 'yyyy-MM'));
    if (bucket) {
      bucket.sales += d.total_sale_value || 0;
      bucket.cost += d.total_cost_value || 0;
      bucket.profit += (d.total_sale_value || 0) - (d.total_cost_value || 0);
    }
  });
  const thisMonth = profitTrend[profitTrend.length - 1];

  // Approved expenses per month → net = gross profit − expenses
  profitTrend.forEach((m) => { m.expenses = 0; });
  expenseList.forEach((e) => {
    if (e.status !== 'Approved') return;
    const bucket = profitTrend.find((b) => b.key === format(new Date(e.date), 'yyyy-MM'));
    if (bucket) bucket.expenses += e.amount || 0;
  });
  profitTrend.forEach((m) => { m.net = m.profit - m.expenses; });

  // Top clients by sales
  const topClients = [...clientRows]
    .filter((c) => c.total_sale_value > 0)
    .sort((a, b) => b.total_sale_value - a.total_sale_value)
    .slice(0, 6);

  // Valuations, receivables & low-stock alerts
  const greenCostFor = (catId) => costs.find((c) => c.category_id === catId)?.green_cost_per_kg || 0;
  const roastedCostFor = (catId, lvl) => costs.find((c) => c.category_id === catId && c.roast_level === lvl)?.cost_per_kg || 0;
  const greenValue = cats.reduce((s, c) => s + (c.current_stock_kg || 0) * greenCostFor(c.id), 0);
  const roastedValue = stockRows.reduce((s, r) => s + (r.quantity_kg || 0) * roastedCostFor(r.category_id, r.roast_level), 0);
  const totalOutstanding = clientRows.reduce((s, c) => s + (c.outstanding || 0), 0);
  const lowStockCats = cats.filter((c) => (c.min_stock_kg || 0) > 0 && (c.current_stock_kg || 0) < c.min_stock_kg);

  const handleDownloadPdf = () => {
    downloadPdfReport({
      title: 'Dashboard Overview',
      subtitle: 'Operations snapshot',
      summary: [
        { label: 'Green Stock', value: `${summary.total_green_stock_kg.toFixed(2)} kg (${rs(greenValue)})` },
        { label: 'Roasted Stock', value: `${summary.total_roasted_stock_kg.toFixed(2)} kg (${rs(roastedValue)})` },
        { label: 'Total Dispatched', value: `${summary.total_dispatched_kg.toFixed(2)} kg` },
        { label: 'Total Wastage', value: `${summary.total_wastage_kg.toFixed(2)} kg` },
        { label: 'Outstanding Receivables', value: rs(totalOutstanding) },
        { label: 'Categories', value: summary.category_count },
        { label: 'Lifetime Roasts', value: summary.roast_count },
        { label: 'Lifetime Dispatches', value: summary.dispatch_count },
        { label: `Sales (${thisMonth.label})`, value: rs(thisMonth.sales) },
        { label: `Gross Profit (${thisMonth.label})`, value: rs(thisMonth.profit) },
      ],
      tables: [
        {
          title: 'Monthly Net (Last 6 Months) — Gross Profit vs Approved Expenses',
          head: ['Month', 'Sales', 'Est. Cost', 'Gross Profit', 'Expenses', 'Net'],
          rows: profitTrend.map((m) => [m.label, rs(m.sales), rs(m.cost), rs(m.profit), rs(m.expenses), rs(m.net)]),
        },
        {
          title: 'Top Clients by Sales',
          head: ['Client', 'Dispatches', 'Total Kg', 'Sales', 'Gross Profit'],
          rows: topClients.map((c) => [c.name, c.dispatch_count, c.total_kg.toFixed(2), rs(c.total_sale_value), rs(c.gross_profit)]),
        },
        {
          title: 'Green Inventory by Category',
          head: ['Category', 'Stock (kg)'],
          rows: cats.map((c) => [c.name, (c.current_stock_kg || 0).toFixed(2)]),
        },
        {
          title: 'Roasted Stock',
          head: ['Category', 'Roast Level', 'Stock (kg)'],
          rows: stockRows.map((s) => [s.category_name, s.roast_level, (s.quantity_kg || 0).toFixed(2)]),
        },
      ],
    });
  };

  const greenByCat = cats.map((c) => ({ name: c.name, value: c.current_stock_kg || 0 }));
  const roastedByLevel = rstock?.by_roast_level || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Dashboard</h1>
          <p className="text-gray-600 mt-1">Evabrew Roastery — operations overview</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setShowReport(true)} data-testid="open-monthly-report"
            className="flex items-center space-x-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-blue-600 hover:to-indigo-700 transition shadow">
            <FileBarChart className="w-4 h-4" /><span>Monthly Report</span>
          </button>
          <button onClick={handleDownloadPdf} data-testid="download-dashboard-pdf"
            className="flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white px-5 py-2.5 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition shadow">
            <Download className="w-4 h-4" /><span>Download PDF</span>
          </button>
        </div>
      </div>

      {/* Low stock alerts */}
      {lowStockCats.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3" data-testid="dashboard-low-stock-banner">
          <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-semibold text-rose-800 text-sm">Low green stock — time to reorder</p>
            <p className="text-sm text-rose-700 mt-0.5">
              {lowStockCats.map((c) => `${c.name} (${(c.current_stock_kg || 0).toFixed(1)} kg, min ${c.min_stock_kg} kg)`).join(' · ')}
            </p>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard icon={<Package />} label="Green Stock" value={`${summary.total_green_stock_kg.toFixed(1)} kg`} sub={greenValue > 0 ? `worth ${inr(greenValue)}` : null} color="from-green-500 to-emerald-600" />
        <StatCard icon={<Coffee />} label="Roasted Stock" value={`${summary.total_roasted_stock_kg.toFixed(1)} kg`} sub={roastedValue > 0 ? `worth ${inr(roastedValue)}` : null} color="from-amber-500 to-orange-600" />
        <StatCard icon={<Truck />} label="Total Dispatched" value={`${summary.total_dispatched_kg.toFixed(1)} kg`} color="from-blue-500 to-indigo-600" />
        <StatCard icon={<AlertTriangle />} label="Total Wastage" value={`${summary.total_wastage_kg.toFixed(1)} kg`} color="from-red-500 to-pink-600" />
        <StatCard icon={<Wallet />} label="Outstanding" value={inr(totalOutstanding)} sub="unpaid by clients" color="from-rose-500 to-red-600" />
      </div>

      {/* Sales / Cost / Profit trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5" data-testid="profit-trend-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-emerald-600" />
            <h3 className="text-lg font-bold text-gray-800">Sales, Cost & Gross Profit — Last 6 Months</h3>
          </div>
          <div className="flex gap-4 flex-wrap">
            <div>
              <p className="text-[11px] text-gray-500 uppercase">Sales ({thisMonth.label})</p>
              <p className="text-lg font-bold text-emerald-600" data-testid="month-sales-value">{inr(thisMonth.sales)}</p>
            </div>
            <div>
              <p className="text-[11px] text-gray-500 uppercase">Est. Cost ({thisMonth.label})</p>
              <p className="text-lg font-bold text-amber-600" data-testid="month-cost-value">{inr(thisMonth.cost)}</p>
            </div>
            <div>
              <p className="text-[11px] text-gray-500 uppercase">Gross Profit ({thisMonth.label})</p>
              <p className={`text-lg font-bold ${thisMonth.profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="month-profit-value">{inr(thisMonth.profit)}</p>
            </div>
          </div>
        </div>
        {profitTrend.every((m) => m.sales === 0 && m.cost === 0) ? (
          <p className="text-center text-gray-400 py-10 text-sm">
            No sales data yet — add sale prices when logging dispatches to see margins here.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={profitTrend} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis tickFormatter={(v) => `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v, name) => [inr(v), name]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="sales" name="Sales" fill="#10b981" radius={[5, 5, 0, 0]} barSize={26} />
              <Bar dataKey="cost" name="Est. Cost" fill="#f59e0b" radius={[5, 5, 0, 0]} barSize={26} />
              <Line type="monotone" dataKey="profit" name="Gross Profit" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 4, fill: '#2563eb' }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Top Clients + Monthly Net */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5" data-testid="top-clients-card">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-5 h-5 text-indigo-600" />
            <h3 className="text-lg font-bold text-gray-800">Top Clients by Sales</h3>
          </div>
          {topClients.length === 0 ? (
            <p className="text-center text-gray-400 py-10 text-sm">
              No client sales yet — add a client and sale price when logging dispatches.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={Math.max(200, topClients.length * 52)}>
              <BarChart data={topClients} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis type="number" tickFormatter={(v) => `₹${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v, name) => [inr(v), name]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="total_sale_value" name="Sales" fill="#6366f1" radius={[0, 5, 5, 0]} barSize={14} />
                <Bar dataKey="gross_profit" name="Gross Profit" fill="#10b981" radius={[0, 5, 5, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5" data-testid="monthly-net-card">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-bold text-gray-800">Monthly Net</h3>
            </div>
            <div>
              <p className="text-[11px] text-gray-500 uppercase text-right">Net ({thisMonth.label})</p>
              <p className={`text-lg font-bold text-right ${thisMonth.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`} data-testid="month-net-value">
                {inr(thisMonth.net)}
              </p>
            </div>
          </div>
          <p className="text-xs text-gray-500 -mt-2 mb-3">Gross profit from dispatches − approved expenses</p>
          {profitTrend.every((m) => m.profit === 0 && m.expenses === 0) ? (
            <p className="text-center text-gray-400 py-10 text-sm">No profit or expense data yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={profitTrend} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `₹${Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v, name) => [inr(v), name]} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="profit" name="Gross Profit" fill="#10b981" radius={[5, 5, 0, 0]} barSize={20} />
                <Bar dataKey="expenses" name="Expenses" fill="#f43f5e" radius={[5, 5, 0, 0]} barSize={20} />
                <Line type="monotone" dataKey="net" name="Net" stroke="#2563eb" strokeWidth={2.5} dot={{ r: 4, fill: '#2563eb' }} />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Charts: green by category + roasted by level */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Green Stock by Category</h3>
          {greenByCat.length === 0 ? (
            <p className="text-center text-gray-400 py-12 text-sm">No categories yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={greenByCat} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" angle={-15} textAnchor="end" height={50} tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `${v}kg`} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} kg`, 'Stock']} />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {greenByCat.map((entry, idx) => (
                    <Cell key={entry.name} fill={CAT_COLORS[idx % CAT_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Roasted Stock by Level</h3>
          {roastedByLevel.every((l) => l.quantity_kg === 0) ? (
            <p className="text-center text-gray-400 py-12 text-sm">No roasted stock yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={roastedByLevel} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => `${v}kg`} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} kg`, 'Stock']} />
                <Bar dataKey="quantity_kg" radius={[6, 6, 0, 0]}>
                  <Cell fill="#fbbf24" />
                  <Cell fill="#f59e0b" />
                  <Cell fill="#44403c" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Last 14 days roasted trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-5 h-5 text-amber-600" />
          <h3 className="text-lg font-bold text-gray-800">Roasted Output — Last 14 Days</h3>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={fourteenDays} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
            <YAxis tickFormatter={(v) => `${v}kg`} tick={{ fontSize: 12 }} />
            <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} kg`, 'Roasted']} />
            <Line type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3, fill: '#f59e0b' }} activeDot={{ r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Recent roasts & dispatches */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <RecentList
          title="Recent Roasts"
          icon={<Flame className="w-5 h-5 text-orange-600" />}
          items={roasts.slice(0, 5).map((r) => ({
            id: r.id,
            left: `${r.category_name} · ${r.roast_level}`,
            right: `${r.roasted_output_kg.toFixed(1)} kg`,
            sub: format(new Date(r.roast_date), 'MMM dd'),
          }))}
          emptyText="No roasts yet."
        />
        <RecentList
          title="Recent Dispatches"
          icon={<Truck className="w-5 h-5 text-blue-600" />}
          items={dispatches.slice(0, 5).map((d) => ({
            id: d.id,
            left: d.client_name
              ? `${d.client_name}${d.invoice_no ? ` · #${d.invoice_no}` : ''}`
              : (d.items || []).map((it) =>
                  it.blend_name ||
                  ((it.components || []).map((c) => c.category_name).join(' + ')) ||
                  `${it.category_name} · ${it.roast_level}`
                ).join(', ') || 'Dispatch',
            right: `${(d.total_quantity_kg ?? d.quantity_kg ?? 0).toFixed(1)} kg`,
            sub: format(new Date(d.dispatch_date), 'MMM dd'),
          }))}
          emptyText="No dispatches yet."
        />
      </div>

      {showReport && <MonthlyReportModal onClose={() => setShowReport(false)} />}
    </div>
  );
};

const StatCard = ({ icon, label, value, color, sub }) => (
  <div className={`bg-gradient-to-br ${color} rounded-xl shadow-lg p-5 text-white`}>
    <div className="flex justify-between items-start">
      <div>
        <p className="text-white/90 text-xs uppercase tracking-wide">{label}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {sub && <p className="text-xs text-white/85 mt-0.5">{sub}</p>}
      </div>
      <div className="opacity-80">{icon}</div>
    </div>
  </div>
);

const RecentList = ({ title, icon, items, emptyText }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
    <div className="flex items-center gap-2 mb-3">
      {icon}<h3 className="text-lg font-bold text-gray-800">{title}</h3>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-gray-400 py-6 text-center">{emptyText}</p>
    ) : (
      <ul className="divide-y divide-gray-100">
        {items.map((it) => (
          <li key={it.id} className="py-2.5 flex justify-between items-center">
            <div>
              <p className="font-medium text-gray-800 text-sm">{it.left}</p>
              <p className="text-xs text-gray-500">{it.sub}</p>
            </div>
            <p className="font-bold text-gray-800">{it.right}</p>
          </li>
        ))}
      </ul>
    )}
  </div>
);

export default Dashboard;
